import { useContext, useEffect, useRef, useState } from 'react';
import ChevronDown from '../../../Assets/ChevronDownLargeDarkMode.svg';
import { getCategoriesForStore, getCustomCollectionsForStore } from '../../../lib/utils/apiCalls';
import { useOutsideClickForTwoRef } from '../../../Hooks/useOutsideClickForTwoRef';
import { InventoryAddProductFormContext } from '../../../Context/InventoryAddProductFormContext';
import TickMark from '../../../Assets/Modals/TickMark.svg';
import CollectionDropdown from './CollectionDropdown';
import SearchIcon from '../../../Assets/OrderManagement/SearchIcon.svg';

function ProductOrganisation() {
    const categoriesDropdownButtonRef = useRef(null);
    const categoriesDropdownRef = useRef(null);
    const categorySearchInputRef = useRef(null);

    const customCollectionDropdownButtonRef = useRef(null);
    const customCollectionDropdownRef = useRef(null);
    const collectionSearchInputRef = useRef(null);

    useOutsideClickForTwoRef(categoriesDropdownButtonRef, categoriesDropdownRef, () => {
        setCategoryDropdownOpen(false);
        setCategorySearchTerm(''); // Clear search when closing
    });

    useOutsideClickForTwoRef(customCollectionDropdownButtonRef, customCollectionDropdownRef, () => {
        setCustomCollectionDropdownOpen(false);
        setCollectionSearchTerm(''); // Clear search when closing
    });

    const {
        selectedCategories,
        setSelectedCategories,
        selectedCustomCollections,
        setSelectedCustomCollections,
    } = useContext(InventoryAddProductFormContext);

    const [categories, setCategories] = useState([]);
    const [customCollections, setCustomCollections] = useState([]);

    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [customCollectionDropdownOpen, setCustomCollectionDropdownOpen] = useState(false);

    // Search states
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [collectionSearchTerm, setCollectionSearchTerm] = useState('');

    // Filtered lists based on search terms
    const filteredCategories = categories.filter(category => 
        category.categoryName.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const filteredCustomCollections = customCollections.filter(collection => 
        collection.CollectionName.toLowerCase().includes(collectionSearchTerm.toLowerCase())
    );

    const handleCategoryDropdownItemClick = (e, category) => {
        e.stopPropagation();

        let isSelected = selectedCategories.some(selected => selected.id === category.id);

        if(isSelected) {
            let filteredList = selectedCategories.filter(item => item.id !== category.id);
            setSelectedCategories(filteredList);
        }
        else {
            setSelectedCategories([ ...selectedCategories, category]);
        }
    }

    const handleCustomCollectionDropdownItemClick = (e, collection) => {
        e.stopPropagation();

        let isSelected = selectedCustomCollections.some(selected => selected.id === collection.id);

        if(isSelected) {
            let filteredList = selectedCustomCollections.filter(item => item.id !== collection.id);
            setSelectedCustomCollections(filteredList);
        }
        else {
            setSelectedCustomCollections([ ...selectedCustomCollections, collection ]);
        }
    }

    const toggleCategoryDropdown = () => {
        setCategoryDropdownOpen(!categoryDropdownOpen);
        // Focus on search input when dropdown opens
        if (!categoryDropdownOpen) {
            setTimeout(() => {
                categorySearchInputRef.current?.focus();
            }, 100);
        }
    }

    const toggleCustomCollectionDropdown = () => {
        setCustomCollectionDropdownOpen(!customCollectionDropdownOpen);
        // Focus on search input when dropdown opens
        if (!customCollectionDropdownOpen) {
            setTimeout(() => {
                collectionSearchInputRef.current?.focus();
            }, 100);
        }
    }

    const handleCategorySearchChange = (e) => {
        setCategorySearchTerm(e.target.value);
    }

    const handleCollectionSearchChange = (e) => {
        setCollectionSearchTerm(e.target.value);
    }

    // Prevent dropdown from closing when clicking on search input
    const handleSearchInputClick = (e) => {
        e.stopPropagation();
    }

    useEffect(() => {
        const fetchStoreCategories = async () => {
            try {
                const response = await getCategoriesForStore();
                setCategories(response.category);
            }
            catch(err) {
                console.log(err);
            }
        }

        const fetchStoreCustomCollections = async () => {
            try {
                const response = await getCustomCollectionsForStore();
                setCustomCollections(response.collection);
            }
            catch(err) {
                console.log(err);
            }
        }

        fetchStoreCategories();
        fetchStoreCustomCollections();
    }, []);

    return (
        <div className="border border-[#EAEAEA] p-5 rounded-xl flex flex-col gap-4">
            <h1 className='font-semibold text-[18px] leading-[26px] select-none'>Product Organisation</h1>

            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                    <h1 className='select-none cursor-pointer font-medium text-base text-[#121212]'>Select category</h1>

                    <div onClick={toggleCategoryDropdown} ref={categoriesDropdownButtonRef} className={`relative cursor-pointer px-3 py-4 border rounded-[9px] border-[#EAEAEA] bg-[#F5F5F5] flex items-center gap-[10px] ${categoryDropdownOpen && 'border-[#00c853]'}`}>
                        <div className="w-[calc(100%-24px)]">
                            <p className='font-normal select-none text-base text-[#666666] whitespace-nowrap overflow-hidden text-ellipsis'>
                                {selectedCategories.length > 0 ? selectedCategories.map(item => item.categoryName).join(', ') : 'select category'}
                            </p>
                        </div>

                        <div className="w-6 h-6 flex items-center justify-center select-none">
                            <img src={ChevronDown} alt="" />
                        </div>

                        {
                            categoryDropdownOpen && 
                            <div ref={categoriesDropdownRef} className="absolute z-10 top-[58px] left-0 w-full border border-[#EAEAEA] rounded-xl shadow-[0px_0px_12px_0px_#0000003D] max-h-56 overflow-y-scroll bg-[#FFFFFF]">
                                <div className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 bg-[#FFFFFF]" onClick={handleSearchInputClick}>
                                    <div className="w-6 h-6 flex items-center justify-center">
                                        <img src={SearchIcon} alt="" />
                                    </div>

                                    <input 
                                        ref={categorySearchInputRef}
                                        type="text" 
                                        className='w-[calc(100%-24px)] outline-none' 
                                        placeholder='Search category'
                                        value={categorySearchTerm}
                                        onChange={handleCategorySearchChange}
                                    />
                                </div>

                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(category => {
                                        let isSelected = selectedCategories.some(selected => selected.id === category.id);

                                        return (
                                            <div key={category.id} onClick={(e) => handleCategoryDropdownItemClick(e, category)} className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5] cursor-pointer">
                                                <div className={`w-6 h-6 rounded-md ${isSelected ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'} `} >
                                                    <img className='select-none' src={isSelected ? TickMark : undefined} alt="" />
                                                </div>

                                                <p className='font-medium select-none text-base text-[#666666]'>
                                                    {category.categoryName}
                                                </p>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="w-full px-3 py-4 text-center">
                                        <p className='font-medium text-base text-[#999999] select-none'>
                                            No categories found
                                        </p>
                                    </div>
                                )}
                            </div>
                        }
                    </div>
                </div>

                {
                    selectedCategories.length > 0 && 
                    <>
                        {
                            selectedCategories.map(category => {
                                return (
                                    <CollectionDropdown 
                                        key={category.id} 
                                        category={category} 
                                    />
                                )
                            })
                        }
                    </>
                }

                <div className="w-full h-[1px] bg-[#EAEAEA]"></div>

                <div className="flex flex-col gap-2">
                    <h1 className='select-none cursor-pointer font-medium text-base text-[#121212]'>Add to custom collection</h1>

                    <div onClick={toggleCustomCollectionDropdown} ref={customCollectionDropdownButtonRef} className={`relative cursor-pointer px-3 py-4 border rounded-[9px] border-[#EAEAEA] bg-[#F5F5F5] flex items-center gap-[10px] ${customCollectionDropdownOpen && 'border-[#00c853]'}`}>
                        <div className="w-[calc(100%-24px)]">
                            <p className='font-normal select-none text-base text-[#666666] whitespace-nowrap overflow-hidden text-ellipsis'>
                                {selectedCustomCollections.length > 0 ? selectedCustomCollections.map(item => item.CollectionName).join(', ') : 'select custom collection'}
                            </p>
                        </div>

                        <div className="select-none w-6 h-6 flex items-center justify-center">
                            <img src={ChevronDown} alt="" />
                        </div>

                        {
                            customCollectionDropdownOpen && 
                            <div ref={customCollectionDropdownRef} className="absolute z-10 top-[58px] left-0 w-full border border-[#EAEAEA] rounded-xl max-h-56 overflow-y-scroll bg-[#FFFFFF] shadow-[0px_0px_12px_0px_#0000003D]">
                                <div className="px-3 py-4 flex items-center gap-[10px] w-full border-b border-[#EAEAEA] sticky top-0 bg-[#FFFFFF]" onClick={handleSearchInputClick}>
                                    <div className="w-6 h-6 flex items-center justify-center">
                                        <img src={SearchIcon} alt="" />
                                    </div>

                                    <input 
                                        ref={collectionSearchInputRef}
                                        type="text" 
                                        className='w-[calc(100%-24px)] outline-none' 
                                        placeholder='Search custom collection'
                                        value={collectionSearchTerm}
                                        onChange={handleCollectionSearchChange}
                                    />
                                </div>

                                {filteredCustomCollections.length > 0 ? (
                                    filteredCustomCollections.map(collection => {
                                        let isSelected = selectedCustomCollections.some(selected => selected.id === collection.id);

                                        return (
                                            <div key={collection.id} onClick={(e) => handleCustomCollectionDropdownItemClick(e, collection)} className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5] cursor-pointer">
                                                <div className={`w-6 h-6 rounded-md ${isSelected ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'} `} >
                                                    <img className='select-none' src={isSelected ? TickMark : undefined} alt="" />
                                                </div>

                                                <p className='font-medium select-none text-base text-[#666666]'>
                                                    {collection.CollectionName}
                                                </p>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="w-full px-3 py-4 text-center">
                                        <p className='font-medium text-base text-[#999999] select-none'>
                                            No custom collections found
                                        </p>
                                    </div>
                                )}
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductOrganisation;